import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Trophy, X } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WinnerSelectionModal = ({ draw, onClose, onWinnerSelected }) => {
  const [selecting, setSelecting] = useState(false);
  const [winner, setWinner] = useState(null);
  const [animating, setAnimating] = useState(false);

  const handleSelectWinner = async () => {
    setSelecting(true);
    setAnimating(true);

    // Animate for 3 seconds before showing result
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/admin/draws/${draw.id}/select-winner`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setWinner(response.data);
        setAnimating(false);
        toast.success('Winner selected successfully!');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to select winner');
        setAnimating(false);
        setSelecting(false);
      }
    }, 3000);
  };

  const handleClose = () => {
    if (winner) {
      onWinnerSelected();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/90 p-4 backdrop-blur-xl sm:p-6"
        onClick={!animating ? handleClose : undefined}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl rounded-3xl border-2 border-[#A855F7] bg-[#18181b] p-6 text-center sm:p-12"
          data-testid="winner-selection-modal"
        >
          {!selecting && !winner && (
            <>
              <button
                onClick={handleClose}
                data-testid="close-winner-selection-btn"
                className="absolute top-4 right-4 text-[#a1a1aa] hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="flex justify-center mb-6">
                <div className="bg-[#A855F7]/20 p-6 rounded-3xl">
                  <Sparkles className="w-16 h-16 text-[#A855F7]" />
                </div>
              </div>

              <h2 className="text-3xl font-heading font-bold text-white mb-4">
                Select Winner for {draw.title}
              </h2>
              <p className="text-[#a1a1aa] mb-8">
                This will randomly select a winner from all participants and notify them via WhatsApp.
              </p>

              <Button
                onClick={handleSelectWinner}
                data-testid="confirm-select-winner-btn"
                className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold text-lg px-12 py-6 rounded-full"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Select Winner Now
              </Button>
            </>
          )}

          {animating && (
            <div className="space-y-6">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  rotate: { duration: 2, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 1, repeat: Infinity }
                }}
                className="flex justify-center"
              >
                <div className="bg-[#EAB308]/20 p-8 rounded-3xl">
                  <Sparkles className="w-24 h-24 text-[#EAB308]" />
                </div>
              </motion.div>
              <h2 className="text-4xl font-heading font-bold gold-gradient">
                Selecting Winner...
              </h2>
              <p className="text-[#a1a1aa] text-lg">The anticipation builds...</p>
            </div>
          )}

          {winner && !animating && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 15 }}
              className="space-y-6"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="flex justify-center"
              >
                <div className="bg-[#EAB308]/20 p-8 rounded-3xl">
                  <Trophy className="w-24 h-24 text-[#EAB308]" />
                </div>
              </motion.div>

              <div>
                <h2 className="text-5xl font-heading font-bold mb-4">
                  <span className="gold-gradient">Winner Selected!</span>
                </h2>
                <p className="text-2xl text-white font-semibold mb-2">
                  {winner.winner?.name}
                </p>
                <p className="text-[#a1a1aa]">
                  Winning Ticket: <span className="text-[#EAB308] font-bold text-3xl font-heading">#{winner.winning_ticket}</span>
                </p>
              </div>

              <div className="bg-[#27272a] rounded-2xl p-6">
                <p className="text-[#a1a1aa] mb-2">WhatsApp Number</p>
                <p className="text-white text-xl font-semibold">
                  {winner.winner?.whatsapp_number}
                </p>
              </div>

              <div className="pt-4">
                <p className="text-[#a1a1aa] mb-4">
                  Winner has been notified via WhatsApp!
                </p>
                <Button
                  onClick={handleClose}
                  data-testid="close-winner-result-btn"
                  className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-8 py-4 rounded-full"
                >
                  Done
                </Button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WinnerSelectionModal;
